import { Loading } from "../app/Status";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
  EMAIL_DELIVERIES,
  EMAIL_DELIVERY_LABELS,
  formatTicketNumber,
  SUPPORT_EVENT_LABELS,
  SUPPORT_EVENTS,
  type SupportMeResponse,
  type SupportNotificationSettingsDto,
} from "@klantpunt/shared";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import {
  errMsg,
  fmtDateTime,
  notificationsKey,
  notifySettingsKey,
  useNotifySettings,
  useSupportNotifications,
} from "../features/support/api";
import { useSupportEvents } from "../features/support/useSupportEvents";
import "../features/support/support.css";
import { SupportGuard } from "./SupportInbox";

export function SupportNotificationsPage() {
  return (
    <SupportGuard>
      <MyNotifications />
    </SupportGuard>
  );
}

/** Mijn meldingen (docs/HELPCENTER.md §8): beschikbaarheid, voorkeuren per gebeurtenis en de laatste meldingen. */
function MyNotifications() {
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useNotifySettings();
  const list = useSupportNotifications();
  const [form, setForm] = useState<SupportNotificationSettingsDto | null>(null);
  const [busy, setBusy] = useState(false);
  useSupportEvents("support");
  useEffect(() => {
    if (settings.data && !form) setForm(settings.data);
  }, [settings.data, form]);

  async function setAvailable(available: boolean) {
    try {
      await api<SupportMeResponse>("/support/me", { method: "PUT", body: { available } });
      setForm((f) => (f ? { ...f, available } : f));
      void qc.invalidateQueries({ queryKey: notifySettingsKey });
      toast(available ? "Je staat op Beschikbaar" : "Je staat op Afwezig");
    } catch (e) {
      toast(errMsg(e));
    }
  }

  async function save() {
    if (!form) return;
    setBusy(true);
    try {
      const { available: _a, mailEnabled: _m, ...body } = form;
      const d = await api<SupportNotificationSettingsDto>("/support/me/notifications", { method: "PUT", body });
      qc.setQueryData(notifySettingsKey, d);
      setForm(d);
      toast("Meldingen opgeslagen");
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function readAll() {
    await api("/support/notifications/read", { method: "POST", body: {} }).catch(() => {});
    void qc.invalidateQueries({ queryKey: notificationsKey });
  }

  const items = list.data?.items ?? [];
  return (
    <div className="page sp-notify">
      <p className="label">
        <Link to="/platform">Platformbeheer</Link> · <Link to="/platform/support">Support</Link>
      </p>
      <h1 className="dot">Mijn meldingen</h1>
      {settings.data && !settings.data.mailEnabled && (
        <div className="note" role="status">
          E-mail staat nog niet aan op de server. Je keuze geldt zodra dat wel zo is.
        </div>
      )}
      {settings.isPending && <Loading />}
      {form && (
        <>
          <section className="panel" aria-labelledby="avail-h">
            <h2 id="avail-h">Beschikbaarheid</h2>
            <div className="segmented" role="radiogroup" aria-label="Beschikbaarheid">
              {[
                [true, "Beschikbaar"],
                [false, "Afwezig"],
              ].map(([v, l]) => (
                <button
                  key={String(v)}
                  type="button"
                  role="radio"
                  aria-checked={form.available === v}
                  className="segbtn"
                  onClick={() => void setAvailable(v as boolean)}
                >
                  {l as string}
                </button>
              ))}
            </div>
            <p className="muted small">
              Wie afwezig is, krijgt geen nieuwe berichten en wordt overgeslagen bij automatisch toewijzen.
            </p>
          </section>

          <section className="panel" aria-labelledby="events-h">
            <h2 id="events-h">Waarover</h2>
            <table className="sp-prefs">
              <thead>
                <tr>
                  <th scope="col">Gebeurtenis</th>
                  <th scope="col">In de app</th>
                  <th scope="col">E-mail</th>
                </tr>
              </thead>
              <tbody>
                {SUPPORT_EVENTS.map((e) => (
                  <tr key={e}>
                    <th scope="row">
                      {SUPPORT_EVENT_LABELS[e].l}
                      <br />
                      <span className="muted small">{SUPPORT_EVENT_LABELS[e].t}</span>
                    </th>
                    {(["inApp", "email"] as const).map((k) => (
                      <td key={k}>
                        <input
                          type="checkbox"
                          aria-label={`${SUPPORT_EVENT_LABELS[e].l}: ${k === "inApp" ? "in de app" : "e-mail"}`}
                          checked={form.events[e][k]}
                          onChange={(ev) =>
                            setForm({
                              ...form,
                              events: { ...form.events, [e]: { ...form.events[e], [k]: ev.target.checked } },
                            })
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="panel" aria-labelledby="mail-h">
            <h2 id="mail-h">E-mail</h2>
            <label className="field">
              <span>Bezorging</span>
              <select
                value={form.emailDelivery}
                onChange={(e) =>
                  setForm({ ...form, emailDelivery: e.target.value as SupportNotificationSettingsDto["emailDelivery"] })
                }
              >
                {EMAIL_DELIVERIES.map((d) => (
                  <option key={d} value={d}>
                    {EMAIL_DELIVERY_LABELS[d]}
                  </option>
                ))}
              </select>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={form.workHoursOnly}
                onChange={(e) => setForm({ ...form, workHoursOnly: e.target.checked })}
              />
              Alleen tijdens werktijden (ma–vr 8:00–18:00)
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={form.muteWhenAway}
                onChange={(e) => setForm({ ...form, muteWhenAway: e.target.checked })}
              />
              Geen mail als ik op Afwezig sta
            </label>
            <div className="btnrow">
              <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
                {busy ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </section>
        </>
      )}

      <section className="panel" aria-labelledby="recent-h">
        <div className="titlerow">
          <h2 id="recent-h">Laatste meldingen</h2>
          {(list.data?.unread ?? 0) > 0 && (
            <button type="button" className="btn small" onClick={() => void readAll()}>
              Alles gelezen
            </button>
          )}
        </div>
        {list.isSuccess && items.length === 0 && <p className="muted">Nog geen meldingen.</p>}
        <ul className="sp-mylist">
          {items.map((n) => (
            <li key={n.id}>
              <Link to={`/platform/support/${n.ticketId}`} className={`sp-myrow${n.read ? "" : " sp-unread"}`}>
                <span className="code small">{formatTicketNumber(n.ticketNumber)}</span>
                <span className="grow">{n.text}</span>
                <time className="muted small" dateTime={n.createdAt}>
                  {fmtDateTime(n.createdAt)}
                </time>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
